import { useCallback, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "./useDebouncedCallback";

export const useAutoSave = (onSave, wait) => {
  const [autoSave, setAutoSave] = useState();
  const debouncedSave = useDebouncedCallback((saveOptions) => {
    setAutoSave({ someDate: new Date(), saveOptions });
  }, wait);
  const isSaving = useRef(false);
  const saveQueue = useRef([]);

  const processSaveQueue = async () => {
    if (isSaving.current || saveQueue.current.length === 0)
      return;

    isSaving.current = true;

    const saveOptions = saveQueue.current.shift();
    await onSave(saveOptions);

    isSaving.current = false;

    if (saveQueue.current.length > 0) {
      processSaveQueue();
    }
  }

  const queueSave = (saveOptions) => {
    saveQueue.current.push(saveOptions);
    processSaveQueue();
  }

  useEffect(() => {
    if (autoSave) {
      queueSave(autoSave.saveOptions);
    }
  }, [autoSave])

  // Wait until all saves (debounced + queued) are done
  const flushSaves = useCallback(async () => {
    // run pending debounce immediately
    await debouncedSave.flush();

    // Wait for all queue + active save to complete
    while (isSaving.current || saveQueue.current.length > 0) {
      await new Promise((res) => setTimeout(res, 100));
    }
  }, [debouncedSave]);

  return {
    debouncedSave,
    flushSaves,
  }
};

// Session ID
// Formik and rich text editor